import React, { useState, useMemo, useEffect, useRef } from 'react';
import { TrendingUp, TrendingDown, DollarSign, Users, Truck, AlertTriangle, CheckCircle, Info, Shield, Clock } from 'lucide-react';

const fmtMXN = (n) => `$${Math.round(n).toLocaleString('es-MX')}`;
const fmtPct = (n) => `${parseFloat(n).toFixed(1)}%`;

function Slider({ label, value, setValue, min, max, step = 1, suffix = '', hint }) {
  return (
    <div className="mb-3">
      <div className="flex justify-between items-center mb-1">
        <label className="text-xs font-semibold text-gray-600">{label}</label>
        <span className="text-sm font-bold text-gray-800">
          {typeof value === 'number' && !Number.isInteger(value) ? value.toFixed(1) : value}{suffix}
        </span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e => setValue(parseFloat(e.target.value))}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-500" />
      {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
    </div>
  );
}

function NumInput({ label, value, setValue, prefix = '', suffix = '', min = 0 }) {
  return (
    <div className="mb-3">
      <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
      <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
        {prefix && <span className="px-2 text-xs text-gray-500 bg-gray-100 border-r border-gray-300">{prefix}</span>}
        <input type="number" min={min} value={value}
          onChange={e => setValue(Math.max(min, parseFloat(e.target.value) || 0))}
          className="flex-1 px-2 py-1.5 text-sm focus:outline-none" />
        {suffix && <span className="px-2 text-xs text-gray-500 bg-gray-100 border-l border-gray-300">{suffix}</span>}
      </div>
    </div>
  );
}

function MetricCard({ label, value, sub, color = 'gray', icon: Icon }) {
  const colors = {
    green: 'bg-green-50 border-green-200 text-green-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
  };
  return (
    <div className={`rounded-xl border p-3 ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={16} />}
        <span className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</span>
      </div>
      <p className="text-xl font-black">{value}</p>
      {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
    </div>
  );
}

function ReglaNegocio() {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
      <p className="text-xs font-black text-amber-700 uppercase tracking-wide mb-2">Reglas de negocio aplicadas</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs mb-2">
        <div className="flex items-start gap-2">
          <CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
          <span className="text-gray-700"><strong>Persona física → Transportista moral:</strong> bono registro + % por cada flete completado ✅</span>
        </div>
        <div className="flex items-start gap-2">
          <CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
          <span className="text-gray-700"><strong>Persona física → Cliente moral:</strong> bono registro + % cada vez que confirma recepción ✅</span>
        </div>
        <div className="flex items-start gap-2">
          <AlertTriangle size={14} className="text-orange-500 mt-0.5 flex-shrink-0" />
          <span className="text-gray-700"><strong>Persona física → Persona física:</strong> solo construye cadena, sin comisión ⚠️</span>
        </div>
        <div className="flex items-start gap-2">
          <AlertTriangle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
          <span className="text-gray-700"><strong>Empresa moral → Cualquiera:</strong> no puede ser referidor ❌</span>
        </div>
      </div>
      <div className="flex items-start gap-2">
        <Clock size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
        <span className="text-xs text-blue-700">
          <strong>Bonos de registro retenidos hasta primer flete</strong> — protección antifraude. Las comisiones recurrentes se acreditan inmediatamente al completar cada flete.
        </span>
      </div>
    </div>
  );
}

export default function SimuladorRedMercadeo({ configPlataforma }) {
  const cargadoRef = useRef(false);

  // ── PARÁMETROS DEL NEGOCIO ──
  const [comisionChoyero, setComisionChoyero] = useState(20);
  const [valorFlete, setValorFlete] = useState(8000);

  // ── TRANSPORTISTAS ──
  const [fletesMesTrans, setFletesMesTrans] = useState(12);
  const [transActivos, setTransActivos] = useState(30);
  const [nuevosTransMoralMes, setNuevosTransMoralMes] = useState(8);

  // ── CLIENTES MORALES ──
  const [fletesMesCliente, setFletesMesCliente] = useState(4);
  const [clientesActivos, setClientesActivos] = useState(20);
  const [nuevosClientesMoralMes, setNuevosClientesMoralMes] = useState(12);

  // ── CRECIMIENTO / ANTIFRAUDE ──
  const [pctReferido, setPctReferido] = useState(60);
  const [pctActivan, setPctActivan] = useState(70);

  // ── DISTRIBUCIÓN DE NIVELES ──
  const [pctConN2, setPctConN2] = useState(40);
  const [pctConN3, setPctConN3] = useState(15);
  const [pctActivosN1, setPctActivosN1] = useState(50);
  const [pctActivosN2, setPctActivosN2] = useState(25);
  const [pctActivosN3, setPctActivosN3] = useState(10);

  // ── BONOS DE REGISTRO ──
  const [bonoTN1, setBonoTN1] = useState(300);
  const [bonoTN2, setBonoTN2] = useState(150);
  const [bonoTN3, setBonoTN3] = useState(50);
  const [bonoCN1, setBonoCN1] = useState(150);
  const [bonoCN2, setBonoCN2] = useState(75);
  const [bonoCN3, setBonoCN3] = useState(25);

  // ── % FLETE TRANSPORTISTA (recurrente al completar flete) ──
  const [pctTN1, setPctTN1] = useState(3);
  const [pctTN2, setPctTN2] = useState(1.5);
  const [pctTN3, setPctTN3] = useState(0.5);

  // ── % FLETE CLIENTE MORAL (recurrente al confirmar recepción) ──
  const [pctCN1, setPctCN1] = useState(2);
  const [pctCN2, setPctCN2] = useState(1);
  const [pctCN3, setPctCN3] = useState(0.5);

  // Precargar valores desde configPlataforma (solo una vez al montar)
  useEffect(() => {
    if (cargadoRef.current || !configPlataforma) return;
    const red = configPlataforma.redMercadeo;
    if (red) {
      if (red.pctFleteN1        != null) setPctTN1(red.pctFleteN1);
      if (red.pctFleteN2        != null) setPctTN2(red.pctFleteN2);
      if (red.pctFleteN3        != null) setPctTN3(red.pctFleteN3);
      if (red.pctFleteClienteN1 != null) setPctCN1(red.pctFleteClienteN1);
      if (red.pctFleteClienteN2 != null) setPctCN2(red.pctFleteClienteN2);
      if (red.pctFleteClienteN3 != null) setPctCN3(red.pctFleteClienteN3);
      if (red.bonoTransN1       != null) setBonoTN1(red.bonoTransN1);
      if (red.bonoTransN2       != null) setBonoTN2(red.bonoTransN2);
      if (red.bonoTransN3       != null) setBonoTN3(red.bonoTransN3);
      if (red.bonoClienteN1     != null) setBonoCN1(red.bonoClienteN1);
      if (red.bonoClienteN2     != null) setBonoCN2(red.bonoClienteN2);
      if (red.bonoClienteN3     != null) setBonoCN3(red.bonoClienteN3);
    }
    if (configPlataforma.comisionBasico != null) setComisionChoyero(configPlataforma.comisionBasico);
    cargadoRef.current = true;
  }, [configPlataforma]);

  const c = useMemo(() => {
    const comisionPorFlete = valorFlete * (comisionChoyero / 100);

    // VOLUMEN DE FLETES
    const totalFletesTrans   = transActivos * fletesMesTrans;
    const totalFletesCliente = clientesActivos * fletesMesCliente;
    const totalFletes        = totalFletesTrans + totalFletesCliente;

    // INGRESOS BRUTOS DE CHOYERO
    const comisionBrutaTrans   = totalFletesTrans   * comisionPorFlete;
    const comisionBrutaCliente = totalFletesCliente * comisionPorFlete;
    const comisionBruta        = comisionBrutaTrans + comisionBrutaCliente;

    // EMPRESAS REFERIDAS QUE ACTIVAN
    const transRefBruto    = nuevosTransMoralMes    * (pctReferido / 100);
    const clientesRefBruto = nuevosClientesMoralMes * (pctReferido / 100);
    const transRef    = transRefBruto    * (pctActivan / 100);
    const clientesRef = clientesRefBruto * (pctActivan / 100);
    const transFiltrados    = transRefBruto    - transRef;
    const clientesFiltrados = clientesRefBruto - clientesRef;

    // COSTOS BONOS DE REGISTRO (solo se pagan al activar = primer flete)
    const costoRegTrans = transRef * (
      bonoTN1 + (pctConN2 / 100) * bonoTN2 + (pctConN3 / 100) * bonoTN3
    );
    const costoRegClientes = clientesRef * (
      bonoCN1 + (pctConN2 / 100) * bonoCN2 + (pctConN3 / 100) * bonoCN3
    );
    const totalCostoReg = costoRegTrans + costoRegClientes;

    // COSTOS RECURRENTES — FLETES TRANSPORTISTA
    const costoFleteTransN1 = totalFletesTrans * (pctActivosN1 / 100) * comisionPorFlete * (pctTN1 / 100);
    const costoFleteTransN2 = totalFletesTrans * (pctActivosN2 / 100) * comisionPorFlete * (pctTN2 / 100);
    const costoFleteTransN3 = totalFletesTrans * (pctActivosN3 / 100) * comisionPorFlete * (pctTN3 / 100);
    const totalCostoFletesTrans = costoFleteTransN1 + costoFleteTransN2 + costoFleteTransN3;

    // COSTOS RECURRENTES — FLETES CLIENTE MORAL
    const costoFleteClienteN1 = totalFletesCliente * (pctActivosN1 / 100) * comisionPorFlete * (pctCN1 / 100);
    const costoFleteClienteN2 = totalFletesCliente * (pctActivosN2 / 100) * comisionPorFlete * (pctCN2 / 100);
    const costoFleteClienteN3 = totalFletesCliente * (pctActivosN3 / 100) * comisionPorFlete * (pctCN3 / 100);
    const totalCostoFletesCliente = costoFleteClienteN1 + costoFleteClienteN2 + costoFleteClienteN3;

    const totalCostoFletes = totalCostoFletesTrans + totalCostoFletesCliente;
    const totalCostos      = totalCostoReg + totalCostoFletes;
    const ingresosNetos    = comisionBruta - totalCostos;
    const pctCostoSobreComision = comisionBruta > 0 ? (totalCostos / comisionBruta) * 100 : 0;

    // AHORRO ANTIFRAUDE
    const costoSinFiltro = transRefBruto * (bonoTN1 + (pctConN2/100)*bonoTN2 + (pctConN3/100)*bonoTN3)
      + clientesRefBruto * (bonoCN1 + (pctConN2/100)*bonoCN2 + (pctConN3/100)*bonoCN3);
    const ahorroAntifraude = costoSinFiltro - totalCostoReg;

    // PUNTO DE EQUILIBRIO (transportista, que tiene más fletes y es más fácil de calcular)
    const costoRefUnTrans = bonoTN1 + (pctConN2 / 100) * bonoTN2 + (pctConN3 / 100) * bonoTN3;
    const redDescuentoTrans = (pctActivosN1 / 100 * pctTN1 / 100)
      + (pctActivosN2 / 100 * pctTN2 / 100)
      + (pctActivosN3 / 100 * pctTN3 / 100);
    const comisionNetaTrans = comisionPorFlete * (1 - redDescuentoTrans);
    const fletesParaRecuperar = comisionNetaTrans > 0 ? Math.ceil(costoRefUnTrans / comisionNetaTrans) : 0;
    const mesesRecuperar = fletesMesTrans > 0 ? (fletesParaRecuperar / fletesMesTrans).toFixed(1) : '∞';

    // PROYECCIÓN 12 MESES
    let proyeccion = [];
    let transAcum    = transActivos;
    let clientesAcum = clientesActivos;
    for (let mes = 1; mes <= 12; mes++) {
      const fTrans   = transAcum    * fletesMesTrans;
      const fCliente = clientesAcum * fletesMesCliente;
      const comEste  = (fTrans + fCliente) * comisionPorFlete;

      const costoRegEste =
        (nuevosTransMoralMes    * (pctReferido/100) * (pctActivan/100) * (bonoTN1 + (pctConN2/100)*bonoTN2 + (pctConN3/100)*bonoTN3)) +
        (nuevosClientesMoralMes * (pctReferido/100) * (pctActivan/100) * (bonoCN1  + (pctConN2/100)*bonoCN2  + (pctConN3/100)*bonoCN3));

      const costoFleteTransEste = fTrans * (
        (pctActivosN1/100 * pctTN1/100) + (pctActivosN2/100 * pctTN2/100) + (pctActivosN3/100 * pctTN3/100)
      ) * comisionPorFlete;

      const costoFleteClienteEste = fCliente * (
        (pctActivosN1/100 * pctCN1/100) + (pctActivosN2/100 * pctCN2/100) + (pctActivosN3/100 * pctCN3/100)
      ) * comisionPorFlete;

      const costoRedEste = costoRegEste + costoFleteTransEste + costoFleteClienteEste;
      const netoEste     = comEste - costoRedEste;

      proyeccion.push({
        mes,
        transportistas: Math.round(transAcum),
        clientes: Math.round(clientesAcum),
        fletes: Math.round(fTrans + fCliente),
        comision: Math.round(comEste),
        costoReg: Math.round(costoRegEste),
        costoFletesTrans: Math.round(costoFleteTransEste),
        costoFletesCliente: Math.round(costoFleteClienteEste),
        neto: Math.round(netoEste),
        pct: comEste > 0 ? (costoRedEste / comEste * 100).toFixed(1) : '0.0',
      });
      transAcum    += nuevosTransMoralMes;
      clientesAcum += nuevosClientesMoralMes;
    }

    const totalAnualComision  = proyeccion.reduce((s, r) => s + r.comision, 0);
    const totalAnualCostoRed  = proyeccion.reduce((s, r) => s + r.costoReg + r.costoFletesTrans + r.costoFletesCliente, 0);
    const totalAnualNeto      = proyeccion.reduce((s, r) => s + r.neto, 0);

    return {
      comisionPorFlete, totalFletes, totalFletesTrans, totalFletesCliente,
      comisionBruta, comisionBrutaTrans, comisionBrutaCliente,
      transRef, clientesRef, transFiltrados, clientesFiltrados,
      costoRegTrans, costoRegClientes, totalCostoReg,
      costoFleteTransN1, costoFleteTransN2, costoFleteTransN3, totalCostoFletesTrans,
      costoFleteClienteN1, costoFleteClienteN2, costoFleteClienteN3, totalCostoFletesCliente,
      totalCostoFletes, totalCostos, ingresosNetos, pctCostoSobreComision,
      ahorroAntifraude, costoRefUnTrans, comisionNetaTrans,
      fletesParaRecuperar, mesesRecuperar,
      proyeccion, totalAnualComision, totalAnualCostoRed, totalAnualNeto,
    };
  }, [
    comisionChoyero, valorFlete,
    fletesMesTrans, transActivos, nuevosTransMoralMes,
    fletesMesCliente, clientesActivos, nuevosClientesMoralMes,
    pctReferido, pctActivan,
    pctConN2, pctConN3, pctActivosN1, pctActivosN2, pctActivosN3,
    bonoTN1, bonoTN2, bonoTN3, bonoCN1, bonoCN2, bonoCN3,
    pctTN1, pctTN2, pctTN3, pctCN1, pctCN2, pctCN3,
  ]);

  const saludColor = c.pctCostoSobreComision < 10 ? 'green'
    : c.pctCostoSobreComision < 20 ? 'orange' : 'red';

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-black text-gray-800">Simulador de Red Multinivel</h2>
          <p className="text-sm text-gray-500 mt-1">Comisiones por transportistas <strong>y</strong> clientes morales referidos — dos fuentes de ingreso recurrente.</p>
        </div>
        {configPlataforma?.redMercadeo && (
          <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5 shrink-0">
            <CheckCircle size={12} />
            Valores precargados desde configuración real del sistema
          </div>
        )}
      </div>

      <ReglaNegocio />

      {/* MÉTRICAS CLAVE */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Comisión bruta/mes" value={fmtMXN(c.comisionBruta)}
          sub={`${c.totalFletes} fletes × ${fmtMXN(c.comisionPorFlete)}`} color="blue" icon={DollarSign} />
        <MetricCard label="Costo total red/mes" value={fmtMXN(c.totalCostos)}
          sub={`${fmtPct(c.pctCostoSobreComision)} de tu comisión`} color={saludColor} icon={TrendingDown} />
        <MetricCard label="Ingreso neto/mes" value={fmtMXN(c.ingresosNetos)}
          sub="después de pagar la red" color={c.ingresosNetos >= 0 ? 'green' : 'red'} icon={TrendingUp} />
        <MetricCard label="Ahorro antifraude/mes" value={fmtMXN(c.ahorroAntifraude)}
          sub={`${Math.round(c.transFiltrados + c.clientesFiltrados)} registros bloqueados`} color="purple" icon={Shield} />
      </div>

      {/* Alerta de salud */}
      {c.pctCostoSobreComision > 25 && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
          <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-sm font-bold text-red-700">Riesgo — La red consume más del 25% de tu comisión</p>
            <p className="text-xs text-red-600 mt-1">Reduce bonos de registro o los porcentajes por flete.</p>
          </div>
        </div>
      )}
      {c.pctCostoSobreComision <= 10 && (
        <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
          <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
          <p className="text-sm font-bold text-green-700">La red consume solo {fmtPct(c.pctCostoSobreComision)} de tu comisión — sistema sostenible ✅</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* PARÁMETROS */}
        <div className="lg:col-span-1 space-y-4">

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-xs font-black text-gray-700 mb-3 uppercase tracking-wide">Base del Negocio</h3>
            <Slider label="Comisión Choyero" value={comisionChoyero} setValue={setComisionChoyero}
              min={5} max={35} suffix="%" hint={`Por flete de ${fmtMXN(valorFlete)} → ${fmtMXN(c.comisionPorFlete)}`} />
            <NumInput label="Valor promedio de flete" value={valorFlete} setValue={setValorFlete} prefix="$" suffix="MXN" />
          </div>

          <div className="bg-white rounded-xl border border-orange-200 p-4">
            <h3 className="text-xs font-black text-orange-700 mb-3 uppercase tracking-wide flex items-center gap-1">
              <Truck size={13} /> Transportistas
            </h3>
            <Slider label="Transportistas activos hoy" value={transActivos} setValue={setTransActivos} min={1} max={500} />
            <Slider label="Fletes por transportista/mes" value={fletesMesTrans} setValue={setFletesMesTrans} min={1} max={60}
              hint={`${c.totalFletesTrans} fletes/mes → ${fmtMXN(c.comisionBrutaTrans)} comisión`} />
            <Slider label="Nuevos transportistas morales/mes" value={nuevosTransMoralMes} setValue={setNuevosTransMoralMes} min={0} max={100} />
          </div>

          <div className="bg-white rounded-xl border border-blue-200 p-4">
            <h3 className="text-xs font-black text-blue-700 mb-3 uppercase tracking-wide flex items-center gap-1">
              <Users size={13} /> Clientes Morales
            </h3>
            <Slider label="Clientes morales activos hoy" value={clientesActivos} setValue={setClientesActivos} min={0} max={500} />
            <Slider label="Fletes por cliente/mes" value={fletesMesCliente} setValue={setFletesMesCliente} min={1} max={30}
              hint={`${c.totalFletesCliente} fletes/mes → ${fmtMXN(c.comisionBrutaCliente)} comisión`} />
            <Slider label="Nuevos clientes morales/mes" value={nuevosClientesMoralMes} setValue={setNuevosClientesMoralMes} min={0} max={200} />
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-xs font-black text-gray-700 mb-3 uppercase tracking-wide">Crecimiento / Antifraude</h3>
            <Slider label="% que llegan por referido" value={pctReferido} setValue={setPctReferido} min={0} max={100} suffix="%" />
            <Slider label="% que completan su primer flete" value={pctActivan} setValue={setPctActivan}
              min={0} max={100} suffix="%"
              hint={`Activa bonos de registro. Con "Primer Flete de Prueba" ON este % sube. ${Math.round((c.transFiltrados + c.clientesFiltrados))} empresas/mes no concretan.`} />
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-xs font-black text-gray-700 mb-3 uppercase tracking-wide">Cobertura de Niveles</h3>
            <p className="text-xs text-gray-400 mb-2">De los referidos ¿cuántos tienen N2/N3?</p>
            <Slider label="Con referidor N2" value={pctConN2} setValue={setPctConN2} min={0} max={100} suffix="%" />
            <Slider label="Con referidor N3" value={pctConN3} setValue={setPctConN3} min={0} max={pctConN2} suffix="%" />
            <p className="text-xs text-gray-400 mb-2 mt-2">Empresas activas con referidor activo</p>
            <Slider label="Con N1 activo" value={pctActivosN1} setValue={setPctActivosN1} min={0} max={100} suffix="%" />
            <Slider label="Con N2 activo" value={pctActivosN2} setValue={setPctActivosN2} min={0} max={pctActivosN1} suffix="%" />
            <Slider label="Con N3 activo" value={pctActivosN3} setValue={setPctActivosN3} min={0} max={pctActivosN2} suffix="%" />
          </div>
        </div>

        {/* RESULTADOS */}
        <div className="lg:col-span-2 space-y-4">

          {/* Bonos + % flete */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-xs font-black text-gray-700 mb-4 uppercase tracking-wide">Estructura de Comisiones</h3>

            {/* Bonos de registro */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-xs font-bold text-orange-600 mb-2 flex items-center gap-1">
                  <Truck size={12} /> Bono registro transportista
                </p>
                {[[bonoTN1,setBonoTN1,'N1'],[bonoTN2,setBonoTN2,'N2'],[bonoTN3,setBonoTN3,'N3']].map(([v,s,l],i) => (
                  <div key={i} className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-gray-500 w-8">{l}</span>
                    <div className="flex items-center border border-gray-300 rounded overflow-hidden flex-1">
                      <span className="px-1.5 text-xs bg-gray-100 border-r border-gray-300">$</span>
                      <input type="number" min={0} value={v}
                        onChange={e => s(Math.max(0,parseInt(e.target.value)||0))}
                        className="flex-1 px-2 py-1 text-sm focus:outline-none" />
                    </div>
                  </div>
                ))}
                <p className="text-xs text-orange-600 mt-1 font-semibold">
                  Prom. por trans: {fmtMXN(bonoTN1 + (pctConN2/100)*bonoTN2 + (pctConN3/100)*bonoTN3)}
                </p>
              </div>
              <div>
                <p className="text-xs font-bold text-blue-600 mb-2 flex items-center gap-1">
                  <Users size={12} /> Bono registro cliente moral
                </p>
                {[[bonoCN1,setBonoCN1,'N1'],[bonoCN2,setBonoCN2,'N2'],[bonoCN3,setBonoCN3,'N3']].map(([v,s,l],i) => (
                  <div key={i} className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-gray-500 w-8">{l}</span>
                    <div className="flex items-center border border-gray-300 rounded overflow-hidden flex-1">
                      <span className="px-1.5 text-xs bg-gray-100 border-r border-gray-300">$</span>
                      <input type="number" min={0} value={v}
                        onChange={e => s(Math.max(0,parseInt(e.target.value)||0))}
                        className="flex-1 px-2 py-1 text-sm focus:outline-none" />
                    </div>
                  </div>
                ))}
                <p className="text-xs text-blue-600 mt-1 font-semibold">
                  Prom. por cliente: {fmtMXN(bonoCN1 + (pctConN2/100)*bonoCN2 + (pctConN3/100)*bonoCN3)}
                </p>
              </div>
            </div>

            {/* % por flete — dos columnas */}
            <div className="border-t pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-bold text-orange-600 mb-2 flex items-center gap-1">
                  <Truck size={12} /> % sobre comisión Choyero — transportista
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {[[pctTN1,setPctTN1,'N1'],[pctTN2,setPctTN2,'N2'],[pctTN3,setPctTN3,'N3']].map(([v,s,l],i) => (
                    <div key={i}>
                      <label className="text-xs text-gray-500">{l}</label>
                      <div className="flex items-center border border-gray-300 rounded overflow-hidden mt-1">
                        <input type="number" min={0} max={20} step={0.5} value={v}
                          onChange={e => s(Math.max(0,parseFloat(e.target.value)||0))}
                          className="flex-1 px-2 py-1 text-sm focus:outline-none" />
                        <span className="px-1.5 text-xs bg-gray-100 border-l border-gray-300">%</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">= {fmtMXN(c.comisionPorFlete * v / 100)}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-bold text-blue-600 mb-2 flex items-center gap-1">
                  <Users size={12} /> % sobre comisión Choyero — cliente moral
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {[[pctCN1,setPctCN1,'N1'],[pctCN2,setPctCN2,'N2'],[pctCN3,setPctCN3,'N3']].map(([v,s,l],i) => (
                    <div key={i}>
                      <label className="text-xs text-gray-500">{l}</label>
                      <div className="flex items-center border border-gray-300 rounded overflow-hidden mt-1">
                        <input type="number" min={0} max={20} step={0.5} value={v}
                          onChange={e => s(Math.max(0,parseFloat(e.target.value)||0))}
                          className="flex-1 px-2 py-1 text-sm focus:outline-none" />
                        <span className="px-1.5 text-xs bg-gray-100 border-l border-gray-300">%</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">= {fmtMXN(c.comisionPorFlete * v / 100)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Desglose mensual */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-xs font-black text-gray-700 mb-3 uppercase tracking-wide">Desglose Mes Actual</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <p className="text-xs font-bold text-gray-500 uppercase">Ingresos Choyero</p>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 flex items-center gap-1"><Truck size={11} className="text-orange-500" /> Trans. ({c.totalFletesTrans} fletes)</span>
                  <span className="font-semibold text-orange-700">{fmtMXN(c.comisionBrutaTrans)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 flex items-center gap-1"><Users size={11} className="text-blue-500" /> Clientes ({c.totalFletesCliente} fletes)</span>
                  <span className="font-semibold text-blue-700">{fmtMXN(c.comisionBrutaCliente)}</span>
                </div>
                <div className="flex justify-between text-sm border-t pt-1">
                  <span className="font-bold text-gray-700">Total comisión bruta</span>
                  <span className="font-black text-blue-700">{fmtMXN(c.comisionBruta)}</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <p className="text-xs font-bold text-gray-500 uppercase">Costos Red</p>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Bonos trans. ({c.transRef.toFixed(1)} activan)</span>
                  <span className="text-red-600">−{fmtMXN(c.costoRegTrans)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Bonos clientes ({c.clientesRef.toFixed(1)} activan)</span>
                  <span className="text-red-600">−{fmtMXN(c.costoRegClientes)}</span>
                </div>
                <div className="border-t pt-1 space-y-1">
                  <div className="flex justify-between text-xs text-orange-600">
                    <span>% recurrente trans. N1/N2/N3</span>
                    <span>−{fmtMXN(c.costoFleteTransN1 + c.costoFleteTransN2 + c.costoFleteTransN3)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-blue-600">
                    <span>% recurrente cliente N1/N2/N3</span>
                    <span>−{fmtMXN(c.costoFleteClienteN1 + c.costoFleteClienteN2 + c.costoFleteClienteN3)}</span>
                  </div>
                </div>
                <div className="flex justify-between text-sm border-t pt-1">
                  <span className="font-bold text-gray-700">Total costos red</span>
                  <span className="font-black text-red-700">−{fmtMXN(c.totalCostos)}</span>
                </div>
                <div className="flex justify-between text-xs text-purple-600">
                  <span>Bloqueado antifraude ({(c.transFiltrados + c.clientesFiltrados).toFixed(1)} emp.)</span>
                  <span className="font-bold">+{fmtMXN(c.ahorroAntifraude)} ahorrado</span>
                </div>
              </div>
            </div>
            <div className={`mt-3 p-3 rounded-lg flex justify-between items-center ${c.ingresosNetos >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
              <span className="font-bold text-gray-700">Ingreso neto Choyero</span>
              <span className={`text-lg font-black ${c.ingresosNetos >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {fmtMXN(c.ingresosNetos)}
                <span className="text-sm font-normal ml-1">({fmtPct(100 - c.pctCostoSobreComision)} retenido)</span>
              </span>
            </div>
            <p className="text-xs text-purple-700 mt-2 bg-purple-50 rounded p-2">
              ⏱ Punto de equilibrio por transportista referido: <strong>{c.fletesParaRecuperar} fletes ≈ {c.mesesRecuperar} meses</strong> para recuperar el bono pagado
            </p>
          </div>

          {/* Proyección 12 meses */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex flex-wrap justify-between items-center mb-3 gap-2">
              <h3 className="text-xs font-black text-gray-700 uppercase tracking-wide">Proyección 12 Meses</h3>
              <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                <span>Comisión: <strong className="text-blue-700">{fmtMXN(c.totalAnualComision)}</strong></span>
                <span>Costo red: <strong className="text-red-600">{fmtMXN(c.totalAnualCostoRed)}</strong></span>
                <span>Neto: <strong className={c.totalAnualNeto >= 0 ? 'text-green-700' : 'text-red-700'}>{fmtMXN(c.totalAnualNeto)}</strong></span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 uppercase tracking-wide">
                    <th className="px-2 py-2 text-left">Mes</th>
                    <th className="px-2 py-2 text-right text-orange-600">Trans.</th>
                    <th className="px-2 py-2 text-right text-blue-600">Clientes</th>
                    <th className="px-2 py-2 text-right">Fletes</th>
                    <th className="px-2 py-2 text-right text-blue-600">Comisión</th>
                    <th className="px-2 py-2 text-right text-red-500">Costo Red</th>
                    <th className="px-2 py-2 text-right text-green-600">Neto</th>
                    <th className="px-2 py-2 text-right">% Red</th>
                  </tr>
                </thead>
                <tbody>
                  {c.proyeccion.map((r) => (
                    <tr key={r.mes} className={`border-t border-gray-100 ${r.mes % 2 === 0 ? 'bg-gray-50' : ''}`}>
                      <td className="px-2 py-1.5 font-semibold">Mes {r.mes}</td>
                      <td className="px-2 py-1.5 text-right text-orange-600">{r.transportistas}</td>
                      <td className="px-2 py-1.5 text-right text-blue-600">{r.clientes}</td>
                      <td className="px-2 py-1.5 text-right text-gray-600">{r.fletes}</td>
                      <td className="px-2 py-1.5 text-right font-semibold text-blue-700">{fmtMXN(r.comision)}</td>
                      <td className="px-2 py-1.5 text-right text-red-600">−{fmtMXN(r.costoReg + r.costoFletesTrans + r.costoFletesCliente)}</td>
                      <td className={`px-2 py-1.5 text-right font-bold ${r.neto >= 0 ? 'text-green-700' : 'text-red-700'}`}>{fmtMXN(r.neto)}</td>
                      <td className={`px-2 py-1.5 text-right font-semibold ${parseFloat(r.pct) > 20 ? 'text-red-600' : parseFloat(r.pct) > 10 ? 'text-orange-500' : 'text-green-600'}`}>{r.pct}%</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-800 text-white font-bold">
                    <td className="px-2 py-2 rounded-bl-lg" colSpan={4}>TOTAL ANUAL</td>
                    <td className="px-2 py-2 text-right text-blue-300">{fmtMXN(c.totalAnualComision)}</td>
                    <td className="px-2 py-2 text-right text-red-300">−{fmtMXN(c.totalAnualCostoRed)}</td>
                    <td className={`px-2 py-2 text-right ${c.totalAnualNeto >= 0 ? 'text-green-300' : 'text-red-300'}`}>{fmtMXN(c.totalAnualNeto)}</td>
                    <td className="px-2 py-2 text-right rounded-br-lg text-gray-300">
                      {fmtPct(c.totalAnualComision > 0 ? c.totalAnualCostoRed / c.totalAnualComision * 100 : 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl p-3">
            <Info size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700">
              <strong>Supuestos:</strong> Los bonos de registro se pagan solo cuando la empresa completa su primer flete (antifraude).
              Los % por flete aplican sobre la comisión de Choyero, no sobre el valor bruto del flete.
              El transportista genera comisión cuando el chofer marca la entrega; el cliente genera comisión cuando confirma la recepción.
              La proyección suma nuevos trans. y clientes morales al base activo cada mes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
